import { FC, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { CASE_TYPE } from "../../core/constants/case.constants"
import { resetCasedata } from "../../core/app/slices/case/caseSlice"
import { clearCache } from "@/core/app/slices/cache/cacheSlice"
import {
  matchPath,
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
  useSearchParams
} from "react-router-dom"
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"
import ErrorOutlinedIcon from "@mui/icons-material/ErrorOutlined"

import { UText } from "../../components"
import {
  Box,
  Button,
  Container,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Backdrop,
  CircularProgress,
} from "../../components/mui.components"
import PatientInfoHeader from "../../containers/LandingLayout/PatientInfoHeader/PatientInfoHeader"
import { setData, setIsCloseTab } from "../../core/app/slices/patients/patientSlice"
import { useAppDispatch, useAppSelector } from "../../core/app/hooks"
import {
  cleanUpDataforNew,
  resetCase,
  setClinicalData,
  setIsScansRendered,
} from "../../core/app/slices/clinical/clinicalSlice"
import { downloadPhotographs1_1 } from "@/core/app/slices/records/photograph/photographThunkApi"
import { uploadProfilePhoto } from "@/core/app/slices/records/xrays/xraysThunkApi"
import { setIsRouteChangeBlocked } from "../../core/app/slices/navigationPopup/navigationPopupSlice"
import {
  resetPhotoLists,
  setPhotoTypeJson,
} from "../../core/app/slices/records/photograph/photographSlice"
import { resetXrays } from "../../core/app/slices/records/xrays/xraysSlice"
import { RootState } from "../../core/app/store"
import { useMyNavigation } from "../../hooks/useMyNavigation"

import Photograph from "./Photograph/Photograph"
import Scans from "./Scans/Scans"
import Xrays from "./Xrays/Xray"
import DentalMoudle from "./Dental/dental"
import RxFormMoudle from "./RxForm/rxForm"
import { updateCaseStatus } from "@/core/app/slices/case/caseThunkApi"
import { CASE_STATUS } from "../Patient/config/status.config"
import { checkIsThereASpecifiedPhoto } from "@/UDTreat/left/utils"
import { setCaseRAtype } from "@/UDTreat/udTreatSlice"
import { caseManagement } from "@/gluelayer"
import { safeCloseWindow } from "@/core/app/CloseTabContext"
import NavigationBlockPopup from "@/components/NavigationBlockPopup/NavigationBlockPopup"
import { setAlert } from "@/core/app/slices/alert/alertSlice"

export interface IRecordRoutesProps {
  showError: boolean
  xrayErrors: boolean
  scanErrors: boolean
  photographErrors: boolean
  setScanErrors: (value: boolean) => void
  setPhotographErrors: (value: boolean) => void
  setXrayErrors: (value: boolean) => void
  flagNextBtn: boolean
  steps?: { id: string; lable: string; to: string }[]
  setIsDragEmpty: (value: boolean) => void
  isAlertOpen: boolean
  setIsAlertOpen: (value: boolean) => void
  isDragOpen: boolean
  setIsDragOpen: (value: boolean) => void
  handleSkip: () => void
  dentalGoNext: boolean | undefined
  setDentalGoNext: (arg: undefined | boolean) => void
  saveAndContinueLater?: boolean
  setSaveAndContinueLater?: (value: boolean) => void
  needDoPresetup: boolean
  setNeedDoPresetup: (value: boolean) => void
  setAiConnecting: (value: boolean) => void
  aiConnecting: boolean
}

const RecordRoutes: FC<IRecordRoutesProps> = ({
  showError,
  xrayErrors,
  scanErrors,
  photographErrors,
  setScanErrors,
  setPhotographErrors,
  setXrayErrors,
  flagNextBtn,
  steps,
  setIsDragEmpty,
  isAlertOpen,
  setIsAlertOpen,
  isDragOpen,
  setIsDragOpen,
  handleSkip,
  dentalGoNext,
  setDentalGoNext,
  saveAndContinueLater,
  setSaveAndContinueLater,
  needDoPresetup,
  setNeedDoPresetup,
  setAiConnecting,
  aiConnecting
}) => {
  const { t } = useTranslation("common")
  return (
    <Routes>
      <Route
        path={"scans"}
        element={
          <Scans
            setScanErrors={setScanErrors}
            flagClickNext={flagNextBtn}
            steps={steps}
            handleSkip={handleSkip}
            caseType={"new_scan"}
            saveAndContinueLater={saveAndContinueLater}
            setSaveAndContinueLater={setSaveAndContinueLater}
            needDoPresetup={needDoPresetup}
            setNeedDoPresetup={setNeedDoPresetup}
            setAIConnecting={setAiConnecting}
            aiConnecting={aiConnecting}
          />
        }
      />
      <Route
        path={"photograph"}
        element={
          <Photograph
            title={t("records.photograph.title")}
            setPhotographErrors={setPhotographErrors}
            setIsDragEmpty={setIsDragEmpty}
            isAlertOpen={isAlertOpen}
            setIsAlertOpen={setIsAlertOpen}
            setIsDragOpen={setIsDragOpen}
            isDragOpen={isDragOpen}
          />
        }
      />
      <Route
        path={"xray"}
        element={
          <Xrays
            title={t("records.xray.title")}
            showError={showError}
            scanErrors={scanErrors}
            photographErrors={photographErrors}
            xrayErrors={xrayErrors}
            setXrayErrors={setXrayErrors}
          />
        }
      />
      <Route
        path={"dentalchart"}
        element={
          <DentalMoudle
            dentalGoNext={dentalGoNext}
            setDentalGoNext={setDentalGoNext}
            saveAndContinueLater={saveAndContinueLater}
          />
        }
      />
      <Route path={"rxForm/*"} element={<RxFormMoudle />} />
      <Route path={"*"} element={<Navigate to={"scans"} replace />} />
    </Routes>
  )
}

const RecordModule: FC = () => {
  // order type
  const { caseRAtype } = useAppSelector(
    (state: RootState) => state.udTreatService,
  )
  const { patientId, caseId } = useParams()
  const [recordsStepper, setRecordsStepper] = useState([])
  const navigate = useMyNavigation()
  const { t } = useTranslation("common")
  const dispatch = useAppDispatch()

  const [activeStep, setActiveStep] = useState<number>(0)
  const {
    isPhotoUploading,
    fileList,
    compositePhoto,
    individualPhotos,
    isDownloading,
    count,
    individualPhotosCount,
    photoTypeJson,
  } = useAppSelector((state: RootState) => state.PhotosService)
  const { isCloseTab } = useAppSelector( state => state.patientService )
  const { loading: xrayLoading, xrayUploadLoading } = useAppSelector(
    (state: RootState) => state.xrayService,
  )
  const { preferences } = useAppSelector(
    (state: RootState) => state.userService?.user,
  )
  const { isScansRendered, isLoading } = useAppSelector(
    (state: RootState) => state.clinicalService,
  )
  const { caseDetail } = useAppSelector((state: RootState) => state.caseService)
  const [skipped, setSkipped] = useState(new Set<number>())
  const [flagNextBtn, setFlagNextBtn] = useState()
  const [isDragOpen, setIsDragOpen] = useState<boolean>(false)

  const { isRouteChangeBlocked } = useAppSelector(
    (state: RootState) => state.navigationPopup,
  )
  const [searchParams, setSearchParams] = useSearchParams()
  const from = searchParams.get("from")
  const action = searchParams.get("action")
  const mode = searchParams.get('mode')
  const [needDoPresetup, setNeedDoPresetup] = useState<boolean>(true)
  const [aiConnecting, setAiConnecting] = useState<boolean>(false)
  const location = useLocation()

  const isRetainerFastTrackOverview = searchParams.get('isRetainerFastrack')

  useEffect(() => {
    if (caseRAtype === CASE_TYPE.RETAINER) {
      setRecordsStepper([
        {
          id: "scans",
          label: "Scans",
          to: `/records/patient/${patientId}/case/${caseId}/scans${location.search}`,
        },
        // removed optional photos upload for retainer case
        // {
        //   id: "photographs",
        //   label: "Photos & X-Rays",
        //   to: `/records/patient/${patientId}/case/${caseId}/photograph`,
        // },

        {
          id: "rx",
          label: "Prescription",
          to: `/records/patient/${patientId}/case/${caseId}/rxForm/prescription${location.search}`,
        },
      ])
    } else if (caseRAtype === CASE_TYPE.ALIGNER) {
      setRecordsStepper([
        {
          id: "scans",
          label: "Scans",
          to: `/records/patient/${patientId}/case/${caseId}/scans${location.search}`,
        },
        {
          id: "photographs",
          label: "Photos & X-Rays",
          to: `/records/patient/${patientId}/case/${caseId}/photograph${location.search}`,
        },
        {
          id: "dental",
          label: "Dental Chart",
          to: `/records/patient/${patientId}/case/${caseId}/dentalchart${location.search}`,
        },
        {
          id: "rx",
          label: "Prescription",
          to: `/records/patient/${patientId}/case/${caseId}/rxForm${location.search}`,
        },
      ])
    } else if (caseRAtype === CASE_TYPE.SELFPLAN) {
      setRecordsStepper([
        {
          id: "scans",
          label: "Scans",
          to: `/records/patient/${patientId}/case/${caseId}/scans${location.search}`,
        },
        {
          id: "photographs",
          label: "Photos & X-Rays",
          to: `/records/patient/${patientId}/case/${caseId}/photograph${location.search}`,
        },
      ])
    }
  }, [caseRAtype])

  useEffect(() => {
    setFlagNextBtn(undefined)
    if (location?.pathname.includes("/scans")) {
      setActiveStep(0)
    } else if (location?.pathname.includes("/photograph")) {
      setActiveStep(1)
    } else if (location?.pathname.includes("/dental")) {
      setActiveStep(2)
    } else if (location?.pathname.includes("/rxForm")) {
      caseRAtype === CASE_TYPE.RETAINER ? setActiveStep(1) : setActiveStep(3)
    } else {
      setActiveStep(0)
    }
  }, [location, caseRAtype])

  useEffect(() => {
    dispatch(cleanUpDataforNew("all"))
    dispatch(resetXrays())
    dispatch(resetPhotoLists())
    dispatch(setPhotoTypeJson(null))
  }, [dispatch])

  useEffect(() => {
    if (caseRAtype === CASE_TYPE.RETAINER && activeStep === 1 && ![
        CASE_STATUS.FILL_OUT_RX_FORM,
        CASE_STATUS.SUBMIT_TO_UASSIST,
        CASE_STATUS.SUBMITTED_TO_UASSIST,
      ].includes(caseDetail?.caseDisposition)) {
      // When visit the form for retainer case, update the case status to FILL_OUT_RX_FORM
      dispatch(
        updateCaseStatus({
          patientId,
          caseId,
          payload: { case_disposition: CASE_STATUS.FILL_OUT_RX_FORM },
        }),
      )
    }
  }, [activeStep, caseRAtype])

  const [saveAndContinueLater, setSaveAndContinueLater] = useState(false)
  const [scanErrors, setScanErrors] = useState<boolean>(false)
  const [photographErrors, setPhotographErrors] = useState<boolean>(false)
  const [xrayErrors, setXrayErrors] = useState<boolean>(false)
  const [showError, setShowError] = useState<boolean>(false)
  const [isDragEmpty, setIsDragEmpty] = useState<boolean>(false)
  const [isAlertOpen, setIsAlertOpen] = useState<boolean>(false)
  
  const avatarFileName = useRef(null)
  const { formTemplateList } = useAppSelector(
    (state: RootState) => state.formService,
  )

  const isStepOptional = (step: number) => {
    if (scanErrors && step === 0) {
      return true
    }
    // if (photographErrors && step === 1) {
    //   return true
    // }
  }

  const isStepSkipped = (step: number) => {
    return skipped.has(step)
  }
  const handleNext = async () => {
    if (
      isDragEmpty &&
      isDragOpen &&
      preferences &&
      !preferences?.on_show_sort_warning
    ) {
      setIsAlertOpen(true)
    } else if (individualPhotos.length > 0 && !isDragOpen) {
      setIsDragOpen(true)
    } else if (activeStep === 1) {
      const { normalPosition, xrayPosition } =
        checkIsThereASpecifiedPhoto(photoTypeJson)
      if ((photographErrors || scanErrors) && caseRAtype !== CASE_TYPE.RETAINER) {
        setShowError(true)
        setSaveAndContinueLater(false)
        dispatch(setIsRouteChangeBlocked({ isRouteChangeBlocked: true }))
      } else if (
        (!normalPosition || !xrayPosition) &&
        caseRAtype !== CASE_TYPE.RETAINER
      ) {
        setPhotographErrors(true)
        setShowError(true)
        setSaveAndContinueLater(false)
        dispatch(setIsRouteChangeBlocked({ isRouteChangeBlocked: true }))
      } else {
        if (activeStep === 1) {
          // set avatar when finish setting photos.
          if (photoTypeJson) {
            await setAvatarFromJson({ ...photoTypeJson } as any)
          }
        }
        // Handle save and continue later for both retainer and non-retainer cases
        if (saveAndContinueLater) {
          dispatch(setIsRouteChangeBlocked({ isRouteChangeBlocked: false }))
          setSaveAndContinueLater(false)
          setTimeout(() => {
            safeCloseWindow()
          }, 100)
          return
        }

        if (caseRAtype !== CASE_TYPE.RETAINER) {
          let newSkipped = skipped
          if (isStepSkipped(activeStep)) {
            newSkipped = new Set(newSkipped.values())
            newSkipped.delete(activeStep)
          }
          setActiveStep((prevActiveStep) => prevActiveStep + 1)
          setSkipped(newSkipped)
          navigate(recordsStepper[activeStep + 1].to)
        }
      }
    } else if (activeStep === 2) {
      // dental save and go rx
      setDentalGoNext(!dentalGoNext)
      let newSkipped = skipped
      if (isStepSkipped(activeStep)) {
        newSkipped = new Set(newSkipped.values())
        newSkipped.delete(activeStep)
      }
      setActiveStep((prevActiveStep) => prevActiveStep + 1)
      setSkipped(newSkipped)
    } else if (activeStep === 3) {
      // navigate to summary page
      navigate(`/records/patient/${patientId}/case/${caseId}/rxForm/summary${location.search}`)
    }
  }

  useEffect(() => {
    if (saveAndContinueLater) {
      newNextClick()
    }
  }, [saveAndContinueLater])

  /**
   * if there is a smile-photo or a front-photo when creating case,set avatar.
   * @param typeJson
   */
  const setAvatarFromJson = async (typeJson: Object) => {
    const keys = Object.keys(typeJson)
    let smilePhoto: string | undefined, frontPhoto: string | undefined
    for (const key of keys) {
      if (key === "thumbs") {
        continue
      }
      if (typeJson[key] === "front_smile") {
        smilePhoto = key
      } else if (typeJson[key] === "front_normal") {
        frontPhoto = key
      }
    }

    // if there is a smile-photo,upload smile-photo,
    // else there is a front-photo,then upload;
    let uploadAvatar: string | undefined
    if (smilePhoto) {
      uploadAvatar = smilePhoto
    } else if (frontPhoto) {
      uploadAvatar = frontPhoto
    }

    // console.log('111111frontPhoto:::',uploadAvatar);

    if (uploadAvatar && uploadAvatar !== avatarFileName.current) {
      const ret = await dispatch(
        downloadPhotographs1_1({
          patientId,
          caseId,
          fileNames: [uploadAvatar],
        }),
      )
      // console.log('22222222frontPhoto:::',ret)
      if (ret.payload) {
        let file = ret.payload[uploadAvatar]
        const formData = new FormData()
        formData.append("attachment", file)
        // console.log('33333frontPhoto:::',file)
        const uploadAvatarRet = await dispatch(
          uploadProfilePhoto({ patientId, formData, fileName: file.name }),
        )
        avatarFileName.current = uploadAvatar
        // console.log('44444frontPhoto:::',uploadAvatarRet,avatarFileName.current)
      }
    }
  }

  const handleBack = () => {
    if (activeStep <= 0 && isRetainerFastTrackOverview) {
      // navigate to overview
      navigate(-1)
    }
    if (isRouteChangeBlocked) {
      /**
       * if isRouteChangeBlocked is true, try to navigate to the previous step
       * let the navigation block popup allow user to decide navigation
       * if user confirm navigation, active step will be updated by route based useEffect
       */
      navigate(recordsStepper[activeStep - 1].to)
    } else {
      /**
       * if isRouteChangeBlocked is false, just navigate to the previous step and update active step
       */
      setActiveStep((prevActiveStep) => prevActiveStep - 1)
      navigate(recordsStepper[activeStep - 1].to)
    }
  }

  const handleSkip = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1)
    navigate(recordsStepper[activeStep + 1].to)
    setSkipped((prevSkipped) => {
      const newSkipped = new Set(prevSkipped.values())
      newSkipped.add(activeStep)
      return newSkipped
    })
  }

  const isStepFailed = (step: number) => {
    if (showError) {
      if (photographErrors && step === 1) {
        return true
      }
      if (scanErrors && step === 0) {
        return true
      }
      // if (xrayErrors && step === 2) {
      //   return true
      // }
    }
  }

  // useEffect(() => {
  //   if (
  //     compositePhoto.length === 0 &&
  //     individualPhotos.length === 0 &&
  //     !fileList.find((file) => file.file_name.includes("composite")) &&
  //     !fileList.find((file) => file.file_name.includes("individual"))
  //   ) {
  //     setPhotographErrors(true)
  //   } else {
  //     setPhotographErrors(false)
  //   }
  //   if (pano.length === 0) {
  //     setXrayErrors(true)
  //   } else {
  //     setXrayErrors(false)
  //   }
  // }, [compositePhoto, individualPhotos, pano, fileList])
  //Clean up clincal zipNames, zipList, and other data to avoid entering clincal with errors
  useEffect(() => {
    caseManagement.closeCase()
    // dispatch(setData({ caseListFor3Shape: [] }))
    return () => {
      dispatch(resetCase())
      dispatch(setData({ caseListFor3Shape: [], caseDetailForShape: {} }))
    }
  }, [])
  useEffect(() => {
    // Early return if case ID is missing
    if (!caseDetail?.id) return
    // Navigate to choosetype if on /scans and udesign_json is absent
    const isScansPath = location.pathname.includes("/scans")
    const isUdesignMissing = !caseDetail?.udesign_json
    if (!isUdesignMissing) {
      const udesignJson = JSON.parse(caseDetail.udesign_json)

      if (mode === "redopresetup") {
        dispatch(setCaseRAtype(CASE_TYPE.SELFPLAN))
      } else if (searchParams.get("isRetainerFastrack") == "true") {
        dispatch(setCaseRAtype(CASE_TYPE.RETAINER))
      } else if (
        !caseRAtype &&
        (udesignJson.UpperType || udesignJson.LowerType)
      ) {
        const { caseDisposition, extra_info } = caseDetail
        const presetup = extra_info?.presetup?.toLowerCase()

        if (caseDisposition === CASE_STATUS.ACTIVE) {
          if (presetup === "inprogress" || presetup === "completed") {
            // in both scans and photos page, on refresh, if presetup 
            // is started on completed, navigate to clinical page.
            navigate(`/clinical/patient/${patientId}/case/${caseId}/treat${location.search}`)
            return
          } else if (presetup === "failed") {
            if (from === "clinical" && action === "submit_to_uassist") {
              dispatch(setCaseRAtype(CASE_TYPE.ALIGNER))
            } else {
              navigate(`/clinical/patient/${patientId}/case/${caseId}/treat${location.search}`)
            }
            return
          }
        } else {
          const hasAligner = [udesignJson.UpperType, udesignJson.LowerType].includes("A")
          dispatch(setCaseRAtype(hasAligner ? CASE_TYPE.ALIGNER : CASE_TYPE.RETAINER))
        }
      }
    }

    // Return if scans are already rendered
    if (isScansRendered) return

    if (isScansPath && isUdesignMissing && !caseRAtype) {
      navigate(`/patients/choosetype/patient/${patientId}/case/${caseId}${location.search}`)
    } else if (isScansPath && !caseRAtype && !isUdesignMissing) {
      dispatch(setIsScansRendered(true))
    } else if (isScansPath) {
      // Set isScansRendered if we're on /scans
      dispatch(setIsScansRendered(true))
    } else if (isUdesignMissing) {
      // Navigate to choosetype if udesign_json is missing
      navigate(`/patients/choosetype/patient/${patientId}/case/${caseId}${location.search}`)
    } else if (
      !isScansRendered &&
      Object.keys(caseDetail).length &&
      caseDetail?.caseDisposition !== CASE_STATUS.FILL_OUT_RX_FORM &&
      caseDetail?.caseDisposition !== CASE_STATUS.SUBMIT_TO_UASSIST
    ) {
      navigate(`/records/patient/${patientId}/case/${caseId}/scans${location.search}`)
    }
  }, [location.pathname, caseDetail, caseRAtype])
  // dental next bool
  const [dentalGoNext, setDentalGoNext] = useState<boolean | undefined>(
    undefined,
  )

  const handleSelfplanNext = async () => {
    if (activeStep === 1) {
      await dispatch(clearCache({ caseId }))
      await dispatch(resetCasedata())
      navigate(`/clinical/patient/${patientId}/case/${caseId}/treat${location.search}`)
    }
  }

  // next click
  const newNextClick = () => {
    dispatch(setIsRouteChangeBlocked({ isRouteChangeBlocked: false }))

    if (activeStep === 0) {
      dispatch(setClinicalData({ isLoading: "pending" }))
      setFlagNextBtn(!flagNextBtn)
    } else if (isStepOptional(activeStep)) {
      handleSkip()
    } else {
      if (caseRAtype === CASE_TYPE.SELFPLAN) {
        handleSelfplanNext()
      } else {
        handleNext()
      }
    }
  }
  // photo 是否可以下一步判断
  useEffect(() => {
    const { normalPosition, xrayPosition } =
      checkIsThereASpecifiedPhoto(photoTypeJson)
    if (normalPosition && xrayPosition) {
      setPhotographErrors(false)
    } else {
      setPhotographErrors(true)
    }
    setShowError(false)
  }, [photoTypeJson])
  // scan 没上传不能下一步
  useEffect(() => {
    if (scanErrors && activeStep === 0) {
      setShowError(true)
    } else {
      setShowError(false)
    }
  }, [scanErrors])
  
  function scanErrorToast(){
    if( (caseRAtype===CASE_TYPE.RETAINER || caseRAtype===CASE_TYPE.SELFPLAN) && scanErrors ){
      dispatch(
        setAlert({
          message: t("records.errors.scanError"),
          isError: true,
        }),
      )
    }
  }

  return (
    <>
      <PatientInfoHeader
        chipLabel={t("records.description")}
        onBtnCancelClick={() => {
          safeCloseWindow()
        }}
        cancelBtnText={t("button.close")}
        chipSx={{
          background: "#0288D1",
          fontSize: "13px",
          fontWeight: "400px",
          color: "primary.contrastText",
        }}
      />
      <Container component="main" maxWidth={"lg"} sx={{ mb: 4 }}>
        <UText
          sxProp={{ display: "flex", justifyContent: "center" }}
          variant={"h4"}
        >
          {caseRAtype === CASE_TYPE.SELFPLAN? activeStep === 0? t("records.uploadscans") : t("records.uploadphotos") : t("records.title")}
        </UText>
      { caseRAtype !== CASE_TYPE.RETAINER && caseRAtype !== CASE_TYPE.SELFPLAN && <Stepper activeStep={activeStep} alternativeLabel sx={{ pt: 2, pb: 3 }}>
          {recordsStepper.map((s, index) => {
            const stepProps: { completed?: boolean } = {}
            const labelProps: {
              optional?: React.ReactNode
              error?: boolean
            } = {}
            if (showError && isStepFailed(index) && activeStep === index) {
              labelProps.error = true
            }
            if (isStepSkipped(index)) {
              stepProps.completed = false
            }

            return (
              <Step key={s.label} {...stepProps}>
                <StepLabel
                  icon={
                    labelProps.error && (
                      <ErrorOutlinedIcon sx={{ color: "error.main" }} />
                    )
                  }
                  {...labelProps}
                >
                  {s.label}
                </StepLabel>
              </Step>
            )
          })}
        </Stepper>}
        <Paper
          elevation={0}
          sx={{
            my: { xs: 3, md: 2 },
            p: "40px",
            borderRadius: "16px",
            marginTop: "0",
            margin: "0 auto",
            width: "max-content",
          }}
        >
          <RecordRoutes
            xrayErrors={xrayErrors}
            scanErrors={scanErrors}
            photographErrors={photographErrors}
            showError={showError}
            setScanErrors={setScanErrors}
            setPhotographErrors={setPhotographErrors}
            setXrayErrors={setXrayErrors}
            flagNextBtn={flagNextBtn}
            steps={recordsStepper}
            setIsDragEmpty={setIsDragEmpty}
            isAlertOpen={isAlertOpen}
            setIsAlertOpen={setIsAlertOpen}
            isDragOpen={isDragOpen}
            setIsDragOpen={setIsDragOpen}
            handleSkip={handleSkip}
            dentalGoNext={dentalGoNext}
            setDentalGoNext={setDentalGoNext}
            saveAndContinueLater={saveAndContinueLater}
            setSaveAndContinueLater={setSaveAndContinueLater}
            needDoPresetup={needDoPresetup}
            setNeedDoPresetup={setNeedDoPresetup}
            setAiConnecting={setAiConnecting}
            aiConnecting={aiConnecting}
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              pt: 1,
            }}
          >
            {
              <Button
                variant="outlined"
                onClick={handleBack}
                sx={{
                  width: "96px",
                  height: "36px",
                  display:
                    (caseRAtype === CASE_TYPE.RETAINER &&
                      (activeStep === 0 || activeStep === 1)) ||
                    (caseRAtype === CASE_TYPE.ALIGNER &&
                      (activeStep === 0 || activeStep === 3)) ||
                    (caseRAtype === CASE_TYPE.SELFPLAN && activeStep === 0)
                      ? "none"
                      : "inline-flex",
                }}
              >
                <ChevronLeftIcon
                  fontSize={"small"}
                  sx={{ marginRight: "3px" }}
                />
                {t("button.back")}
              </Button>
            }
            <Box sx={{ flex: "1 1 auto" }} />
            <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
              {caseRAtype !== CASE_TYPE.SELFPLAN && <Button
                onClick={() => {
                  setSaveAndContinueLater(true)
                  scanErrorToast()
                }}
                variant="outlined"
                sx={{
                  width: "auto",
                  height: "36px",
                  display:
                    (caseRAtype === CASE_TYPE.ALIGNER && activeStep === 3) ||
                    (caseRAtype === CASE_TYPE.RETAINER && activeStep === 1)
                      ? "none"
                      : "inline-flex",
                }}
                disabled={
                  isPhotoUploading === "pending" ||
                  xrayLoading === "pending" ||
                  xrayUploadLoading === "pending" ||
                  isDownloading === "pending" ||
                  individualPhotosCount !== count ||
                  isLoading === "pending"
                }
              >
                {t("button.saveAndContinueLater")}
              </Button>}
              <Button
                onClick={() => {
                  newNextClick()
                  scanErrorToast()
                  // dispatch(
                  //   setIsRouteChangeBlocked({ isRouteChangeBlocked: false }),
                  // )
                  // if (activeStep === 0) {
                  //   dispatch(setClinicalData({ isLoading: "pending" }))
                  //   setFlagNextBtn(!flagNextBtn)
                  // } else if (isStepOptional(activeStep)) {
                  //   handleSkip()
                  // } else {
                  //   handleNext()
                  // }
                }}
                variant="contained"
                sx={{
                  width: "92px",
                  height: "36px",
                  display:
                    (caseRAtype === CASE_TYPE.ALIGNER && activeStep === 3) ||
                    (caseRAtype === CASE_TYPE.RETAINER && activeStep === 1)
                      ? "none"
                      : "inline-flex",
                }}
                disabled={
                  isPhotoUploading === "pending" ||
                  xrayLoading === "pending" ||
                  xrayUploadLoading === "pending" ||
                  isDownloading === "pending" ||
                  individualPhotosCount !== count ||
                  isLoading === "pending" ||
                  aiConnecting
                }
              >
                {t("button.next")}
                <ChevronRightIcon
                  sx={{ marginLeft: "3px" }}
                  fontSize={"small"}
                />
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
      <NavigationBlockPopup
        cancelNavigation={()=>{
          dispatch(setIsCloseTab(false))
        }}
        confirmNavigation={()=>{
          safeCloseWindow()
        }}
        showPrompt={isCloseTab}
      />
      <Backdrop
        sx={{
          backgroundColor: "transparent",
          zIndex: (theme) => theme.zIndex.modal + 10,
        }}
        open={aiConnecting}
      >
        <CircularProgress key={aiConnecting ? "open" : "closed"} />
      </Backdrop>
    </>
  )
}

export default RecordModule
